import api from "./apiClient";
import config from "../../../config";
import {
  initialStudents,
  nigerianClasses,
  careerPaths,
} from "../../lib/students-data";
import { classesData } from "../../lib/classes-data";
import { initialSubjects } from "../../lib/subject-data";
import { initialAdmins } from "../../lib/admin-data";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StudentRow {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  studentId: string;
  classLevel: string;
  status: string;
  gender?: string;
  careerInterests?: string[];
  traitProfile?: Record<string, number> | null;
  performanceMetrics?: { academicScore: number };
  engagementData?: { lastActive: string };
  academicResults?: AcademicResult[];
  activityLog?: unknown[];
  preferences?: { country?: string; examBody?: string; classLevel?: string };
  parentIds?: string[];
  metadata?: { onboardingCompleted?: boolean };
  createdAt?: string;
}

export interface AcademicResult {
  _id: string;
  type: string;
  label: string;
  status: "pending" | "verified" | "rejected";
  uploadedAt: string;
  fileData?: string;
  fileName?: string;
  adminNote?: string;
}

export interface PaginatedStudents {
  students: StudentRow[];
  total: number;
  page: number;
  pages: number;
}

export interface StudentQuery {
  page?: number;
  limit?: number;
  search?: string;
  classLevel?: string;
  status?: string;
}

export interface ClassRow {
  _id: string;
  name: string;
  level: string;
  section?: string;
  academicSession?: string;
  term?: number;
  studentIds?: string[];
  subjects?: string[];
  capacity?: number;
  isActive?: boolean;
  // merged from legacy lib shape
  students?: number;
  avgScore?: number;
  engagement?: number;
  progress?: number;
}

export interface SubjectRow {
  _id: string;
  name: string;
  code?: string;
  description?: string;
  category?: string;
  classLevels?: string[];
  teacherIds?: string[];
  credits?: number;
  isActive?: boolean;
  examBody?: string;
  careerRelevance?: string[];
  // legacy lib fields
  teacher?: string;
  enrolled?: number;
  icon?: string;
  color?: string;
}

export interface StaffRow {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  subjects?: string[];
  status?: string;
  permissions?: Record<string, boolean>;
  inviteAccepted?: boolean;
}

export interface AssessmentRow {
  _id: string;
  title: string;
  subject: string;
  classLevel: string;
  category: string;
  status: string;
  questions?: unknown[];
  passingScore?: number;
  timeLimit?: number;
  availableFrom?: string;
  availableUntil?: string;
  aiGenerated?: boolean;
  createdAt?: string;
}

export interface ResourceRow {
  _id: string;
  title: string;
  description?: string;
  type: string;
  fileUrl: string;
  subject?: string;
  classLevels?: string[];
  tags?: string[];
  downloads?: number;
  isPublic?: boolean;
  status?: string;
  createdAt?: string;
}

export interface SchoolStats {
  totalStudents: number;
  activeStudents: number;
  maleStudents: number;
  femaleStudents: number;
  totalClasses?: number;
  totalStaff?: number;
  totalAssessments?: number;
  studentEngagement?: number;
}

export interface BulkImportRow {
  firstName: string;
  lastName: string;
  email: string;
  classLevel: string;
  admissionNumber?: string;
}

export interface UploadResultRow {
  studentEmail: string;
  examType: string;
  subject: string;
  score: number;
  grade: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const proto = config.appMode === "prototype";

function mockPage<T>(items: T[], page = 1, limit = 10) {
  const start = (page - 1) * limit;
  return {
    students: items.slice(start, start + limit) as unknown as StudentRow[],
    total: items.length,
    page,
    pages: Math.ceil(items.length / limit),
  };
}

// ---------------------------------------------------------------------------
// Students
// ---------------------------------------------------------------------------

export async function listStudents(
  query?: StudentQuery
): Promise<PaginatedStudents> {
  if (proto) {
    const all = initialStudents.map((s, i) => ({
      _id: s.id,
      firstName: s.name.split(" ")[0],
      lastName: s.name.split(" ")[1] ?? "",
      email: `student${i + 1}@school.com`,
      studentId: s.studentId,
      classLevel: s.class,
      status: "active",
    })) as StudentRow[];
    return mockPage(all, query?.page, query?.limit);
  }
  const { data } = await api.get("/students", { params: query });
  return data.data;
}

export async function getStudent(id: string): Promise<StudentRow> {
  if (proto) {
    const s = initialStudents[0];
    return {
      _id: id,
      firstName: s.name.split(" ")[0],
      lastName: s.name.split(" ")[1] ?? "",
      email: "student@school.com",
      studentId: s.studentId,
      classLevel: s.class,
      status: "active",
      academicResults: [],
      activityLog: [],
    };
  }
  const { data } = await api.get(`/students/${id}`);
  return data.data;
}

export async function updateStudent(
  id: string,
  payload: Partial<StudentRow>
): Promise<StudentRow> {
  if (proto) return { ...payload, _id: id } as StudentRow;
  const { data } = await api.patch(`/students/${id}`, payload);
  return data.data;
}

export async function deleteStudent(id: string): Promise<void> {
  if (proto) return;
  await api.delete(`/students/${id}`);
}

export async function bulkImportStudents(rows: BulkImportRow[]): Promise<{ imported: number }> {
  if (proto) return { imported: rows.length };
  const { data } = await api.post("/reports/import-students", { students: rows });
  return data.data;
}

export async function verifyStudent(id: string): Promise<void> {
  if (proto) return;
  await api.patch(`/students/${id}/verify`);
}

export async function verifyStudentResult(
  studentId: string,
  resultId: string
): Promise<void> {
  if (proto) return;
  await api.patch(`/students/${studentId}/results/${resultId}/verify`);
}

export async function rejectStudentResult(
  studentId: string,
  resultId: string,
  adminNote: string
): Promise<void> {
  if (proto) return;
  await api.patch(`/students/${studentId}/results/${resultId}/reject`, {
    adminNote,
  });
}

// ---------------------------------------------------------------------------
// Classes
// ---------------------------------------------------------------------------

export async function listClasses(): Promise<ClassRow[]> {
  if (proto)
    return classesData.map((c) => ({ _id: c.id, name: c.name, level: c.name, ...c }));
  const { data } = await api.get("/classes");
  return data.data?.classes ?? data.data;
}

export async function getClass(id: string): Promise<ClassRow> {
  if (proto) {
    const c = classesData.find((x) => x.id === id) ?? classesData[0];
    return { _id: c.id, name: c.name, level: c.name, ...c };
  }
  const { data } = await api.get(`/classes/${id}`);
  return data.data;
}

export async function createClass(payload: Partial<ClassRow>): Promise<ClassRow> {
  if (proto) return { _id: Date.now().toString(), ...payload } as ClassRow;
  const { data } = await api.post("/classes", payload);
  return data.data;
}

export async function updateClass(
  id: string,
  payload: Partial<ClassRow>
): Promise<ClassRow> {
  if (proto) return { _id: id, ...payload } as ClassRow;
  const { data } = await api.patch(`/classes/${id}`, payload);
  return data.data;
}

export async function deleteClass(id: string): Promise<void> {
  if (proto) return;
  await api.delete(`/classes/${id}`);
}

export async function getClassStudents(classId: string): Promise<StudentRow[]> {
  if (proto) return [];
  const { data } = await api.get(`/classes/${classId}/students`);
  return data.data?.students ?? data.data;
}

export async function addStudentToClass(
  classId: string,
  studentId: string
): Promise<void> {
  if (proto) return;
  await api.post(`/classes/${classId}/students`, { studentId });
}

export async function removeStudentFromClass(
  classId: string,
  studentId: string
): Promise<void> {
  if (proto) return;
  await api.delete(`/classes/${classId}/students/${studentId}`);
}

// ---------------------------------------------------------------------------
// Subjects
// ---------------------------------------------------------------------------

export async function listSubjects(): Promise<SubjectRow[]> {
  if (proto)
    return initialSubjects.map((s) => ({ _id: s.id, name: s.name, code: s.code, ...s }));
  const { data } = await api.get("/subjects");
  return data.data?.subjects ?? data.data;
}

export async function createSubject(
  payload: Partial<SubjectRow>
): Promise<SubjectRow> {
  if (proto) return { _id: Date.now().toString(), ...payload } as SubjectRow;
  const { data } = await api.post("/subjects", payload);
  return data.data;
}

export async function updateSubject(
  id: string,
  payload: Partial<SubjectRow>
): Promise<SubjectRow> {
  if (proto) return { _id: id, ...payload } as SubjectRow;
  const { data } = await api.patch(`/subjects/${id}`, payload);
  return data.data;
}

export async function deleteSubject(id: string): Promise<void> {
  if (proto) return;
  await api.delete(`/subjects/${id}`);
}

// ---------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------

export async function listStaff(): Promise<StaffRow[]> {
  if (proto)
    return initialAdmins.map((a) => ({
      _id: a.id,
      firstName: a.name.split(" ")[0] ?? a.name,
      lastName: a.name.split(" ")[1] ?? "",
      email: a.email,
      role: a.role,
      status: a.status.toLowerCase(),
      inviteAccepted: true,
    }));
  const { data } = await api.get("/staff");
  return data.data?.staff ?? data.data;
}

export async function inviteStaff(payload: {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  subjects?: string[];
  permissions?: Record<string, boolean>;
}): Promise<StaffRow> {
  if (proto) return { _id: Date.now().toString(), ...payload } as unknown as StaffRow;
  const { data } = await api.post("/staff/invite", payload);
  return data.data;
}

export async function updateStaff(
  id: string,
  payload: Partial<StaffRow>
): Promise<StaffRow> {
  if (proto) return { _id: id, ...payload } as StaffRow;
  const { data } = await api.patch(`/staff/${id}`, payload);
  return data.data;
}

export async function removeStaff(id: string): Promise<void> {
  if (proto) return;
  await api.delete(`/staff/${id}`);
}

// ---------------------------------------------------------------------------
// Assessments
// ---------------------------------------------------------------------------

export async function listAssessments(params?: {
  status?: string;
  classLevel?: string;
  subject?: string;
}): Promise<AssessmentRow[]> {
  if (proto) return [];
  const { data } = await api.get("/assessments", { params });
  return data.data?.assessments ?? data.data;
}

export async function createAssessment(
  payload: Partial<AssessmentRow>
): Promise<AssessmentRow> {
  if (proto) return { _id: Date.now().toString(), ...payload } as AssessmentRow;
  const { data } = await api.post("/assessments", payload);
  return data.data;
}

export async function updateAssessment(
  id: string,
  payload: Partial<AssessmentRow>
): Promise<AssessmentRow> {
  if (proto) return { _id: id, ...payload } as AssessmentRow;
  const { data } = await api.patch(`/assessments/${id}`, payload);
  return data.data;
}

export async function deleteAssessment(id: string): Promise<void> {
  if (proto) return;
  await api.delete(`/assessments/${id}`);
}

export async function getAssessmentResults(id: string): Promise<unknown[]> {
  if (proto) return [];
  const { data } = await api.get(`/assessments/${id}/results`);
  return data.data?.results ?? data.data;
}

export async function listPsychometricAssessments(): Promise<(AssessmentRow & { permitted: boolean })[]> {
  if (proto) return [];
  const { data } = await api.get("/assessments/psychometric");
  return data.data?.assessments ?? data.data ?? [];
}

export async function togglePsychometricPermission(id: string): Promise<{ permitted: boolean }> {
  if (proto) return { permitted: true };
  const { data } = await api.patch(`/assessments/psychometric/${id}/permit`);
  return data.data;
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

function getMockResources(): ResourceRow[] {
  return [];
}

export async function listResources(params?: {
  subject?: string;
  type?: string;
}): Promise<ResourceRow[]> {
  if (proto) return getMockResources();
  const { data } = await api.get("/resources", { params });
  return data.data?.resources ?? data.data;
}

export async function createResource(
  payload: Partial<ResourceRow>
): Promise<ResourceRow> {
  if (proto) return { _id: Date.now().toString(), ...payload } as ResourceRow;
  const { data } = await api.post("/resources", payload);
  return data.data;
}

export async function updateResource(
  id: string,
  payload: Partial<ResourceRow>
): Promise<ResourceRow> {
  if (proto) return { _id: id, ...payload } as ResourceRow;
  const { data } = await api.patch(`/resources/${id}`, payload);
  return data.data;
}

export async function deleteResource(id: string): Promise<void> {
  if (proto) return;
  await api.delete(`/resources/${id}`);
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export async function getStudentReport(studentId: string): Promise<unknown> {
  if (proto) return {};
  const { data } = await api.get(`/reports/students/${studentId}`);
  return data.data;
}

export async function uploadResults(
  results: UploadResultRow[]
): Promise<{ processed: number }> {
  if (proto) return { processed: results.length };
  const { data } = await api.post("/reports/upload-results", { results });
  return data.data;
}

export async function exportStudentsCSV(): Promise<Blob> {
  const HEADERS = ["firstName","lastName","email","studentId","classLevel","admissionNumber","status","currentGPA","attendanceRate","careerInterests"];
  if (proto) {
    const csv = HEADERS.join(",") + "\n";
    return new Blob([csv], { type: "text/csv" });
  }
  const { data } = await api.get("/reports/export-students");
  const rows: Record<string, unknown>[] = data.data?.students ?? [];
  const lines = [
    HEADERS.join(","),
    ...rows.map(r => HEADERS.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
  ];
  return new Blob([lines.join("\n")], { type: "text/csv" });
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export async function broadcastNotification(payload: {
  title: string;
  message: string;
  type?: string;
  category?: string;
  classLevel?: string;
}): Promise<void> {
  if (proto) return;
  await api.post("/notifications/broadcast", payload);
}

// ---------------------------------------------------------------------------
// School profile & stats
// ---------------------------------------------------------------------------

export async function getSchool(): Promise<Record<string, unknown>> {
  if (proto) return { name: "Demo School", code: "DEMO01" };
  const { data } = await api.get("/schools/me");
  return data.data?.school ?? data.data;
}

export async function updateSchool(
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  if (proto) return payload;
  const { data } = await api.patch("/schools/me", payload);
  return data.data;
}

export async function getSchoolStats(): Promise<SchoolStats> {
  if (proto)
    return { totalStudents: 0, activeStudents: 0, maleStudents: 0, femaleStudents: 0 };
  const { data } = await api.get("/schools/me/stats");
  return data.data;
}

export async function updateGameConfig(payload: {
  enabledGames?: string[];
  dailyTimeWindow?: { enabled: boolean; startTime?: string; endTime?: string };
  dailyLimitMinutes?: number;
}): Promise<void> {
  if (proto) return;
  await api.patch("/schools/me/game-config", payload);
}

export async function updateEducationSystem(payload: {
  country?: string;
  examBody?: string;
  classLevels?: string[];
}): Promise<void> {
  if (proto) return;
  await api.patch("/schools/me/education-system", payload);
}

export async function updateBranch(
  id: string,
  payload: Record<string, unknown>
): Promise<void> {
  if (proto) return;
  await api.patch(`/schools/me/branches/${id}`, payload);
}

export async function addBranch(
  payload: Record<string, unknown>
): Promise<void> {
  if (proto) return;
  await api.patch("/schools/me/branches", payload);
}

export async function removeBranch(id: string): Promise<void> {
  if (proto) return;
  await api.delete(`/schools/me/branches/${id}`);
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export interface SchoolEvent {
  _id: string;
  title: string;
  type: string;
  startDate: string;
  endDate?: string;
  location?: string;
  isOnline?: boolean;
  rsvpEnabled?: boolean;
  isPublished?: boolean;
}

export async function listEvents(params?: { limit?: number }): Promise<{ events: SchoolEvent[] }> {
  if (proto) return { events: [] };
  const { data } = await api.get("/events", { params });
  return data.data;
}

export async function rsvpEvent(eventId: string): Promise<void> {
  if (proto) return;
  await api.post(`/events/${eventId}/rsvp`, {});
}

export async function inviteParent(studentId: string, parentEmail: string, parentName: string): Promise<void> {
  if (proto) return;
  await api.post("/schools/me/invite-parent", { studentId, parentEmail, parentName });
}

export async function getMyChildren(): Promise<{ children: any[] }> {
  if (proto) return { children: [] };
  const { data } = await api.get("/students/my-children");
  return data.data;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  if (proto) return;
  await api.post("/auth/change-password", { currentPassword, newPassword });
}

// Re-export constants so pages don't need to import lib files directly
export { nigerianClasses, careerPaths };
